/**
 * shared/rule-forecast-1.ts — RULE-FORECAST-1 canonical eligibility logic.
 *
 * SOURCE OF TRUTH: CANON.md lines 167-186 (locked 2026-05-23).
 *
 * Canon 5-bucket taxonomy driven by composite score (0-100):
 *   Commit:     score >= 80    (timing >= 90 also required)
 *   Best Case:  score >= 50    (timing >= 70 also required; Upside at score 65-79)
 *   Pipeline:   score >= 30    (timing >= 50 also required)
 *   Omit:       score < 30, OR hard stop, OR critical gate-negative
 *
 * NOTE: "Upside" (score 65-79) is a display-layer concept in CANON.md.
 * The shared ForecastBucket type does not yet have a dedicated UPSIDE value
 * because it would require a SQL migration and broad type changes. Until that
 * migration is done, BEST_CASE covers both "Upside" (65-79) and "Best Case"
 * (50-64) at the bucket layer, and the display layer maps score band -> label.
 * TODO: add UPSIDE to ForecastBucket when Bryan confirms the SQL migration.
 *
 * Timing ladder (advisory, not the sole category driver under canon):
 *   Timing >= 90%  → Commit eligible
 *   Timing 80%/70% → Best Case max (cannot Commit)
 *   Timing 60%     → Pipeline only (cannot Best Case)
 *   Timing 50%     → Pipeline + Manager Flag (auto-flag)
 *   Timing < 50%   → Omit (was: Push or Loss — both coerced to OMITTED)
 *
 * The effective forecast bucket =
 *   min( composite-derived bucket, timing-eligible bucket ).
 *
 * COMMIT requires BOTH composite >= 80 AND timing >= 90.
 *
 * This file is consumed by both:
 *   - client/src/lib/forecast-rules.ts (UI hard block)
 *   - server/routes/forecast-gate.ts   (server-side validation, defense in depth)
 *
 * Keep it dependency-free so it imports cleanly into both bundles.
 */

export type ForecastBucket =
  | "COMMIT"
  | "BEST_CASE"
  | "PIPELINE"
  | "PUSH"
  | "LOSS"
  | "OMITTED";

/**
 * Ordinal rank of a bucket — used by `min()` to pick the lower of two.
 * Higher number = more aggressive forecast claim.
 */
export const BUCKET_RANK: Record<ForecastBucket, number> = {
  COMMIT: 5,
  BEST_CASE: 4,
  PIPELINE: 3,
  PUSH: 2,
  LOSS: 1,
  OMITTED: 0,
};

/**
 * Highest bucket the deal is eligible for given the rep-declared timing %.
 * NULL timing is treated as "not yet assessed" — Pipeline max so the rep
 * cannot park a deal in COMMIT before completing CP10.
 */
export function timingEligibleBucket(timingPct: number | null): ForecastBucket {
  if (timingPct == null) return "PIPELINE";
  if (timingPct >= 90) return "COMMIT";
  if (timingPct >= 70) return "BEST_CASE";
  if (timingPct >= 50) return "PIPELINE";
  // Sub-50: deal exits active forecast — Omit (legacy: PUSH).
  return "OMITTED";
}

/**
 * Composite-only bucket — canon bands from CANON.md lines 167-186.
 * BEST_CASE covers both "Upside" (65-79) and "Best Case" (50-64) in the
 * shared type until UPSIDE is added as a distinct ForecastBucket.
 */
export function compositeBucket(compositeScore: number): ForecastBucket {
  if (compositeScore >= 80) return "COMMIT";
  if (compositeScore >= 50) return "BEST_CASE"; // covers Upside (65-79) + Best Case (50-64)
  if (compositeScore >= 30) return "PIPELINE";
  return "OMITTED";
}

/**
 * Effective bucket — the bucket that actually counts toward the forecast.
 * = min(composite-derived, timing-eligible). COMMIT requires both sides.
 *
 * Sub-50 timing always exits to PUSH/LOSS no matter the composite — Bryan:
 * "even if you have something that scores at the highest levels on
 * everything else, but potentially low on the timing part something that
 * major should probably disqualify it from being at least in a commit
 * bucket and put it in the upside for possible".
 */
export function effectiveBucket(
  compositeScore: number,
  timingPct: number | null,
): ForecastBucket {
  // Sub-50 timing: deal exits active forecast — maps to OMITTED (legacy: PUSH).
  if (timingPct != null && timingPct < 50) return "OMITTED";
  const c = compositeBucket(compositeScore);
  const t = timingEligibleBucket(timingPct);
  return BUCKET_RANK[c] <= BUCKET_RANK[t] ? c : t;
}

/**
 * Does the deal at this composite + timing need a manager attention flag?
 * Per spec: timing == 50 → auto-flag for manager review.
 */
export function needsManagerFlag(timingPct: number | null): boolean {
  return timingPct === 50;
}

/**
 * Validate a rep's attempted bucket selection. Used by the UI hard block
 * and (later) by the server-side write path. Returns either OK or a
 * structured rejection the UI can render verbatim.
 */
export interface BucketAttempt {
  attempted: ForecastBucket;
  compositeScore: number;
  timingPct: number | null;
}
export type BucketDecision =
  | { ok: true; effective: ForecastBucket; flagged: boolean }
  | {
      ok: false;
      effective: ForecastBucket;
      flagged: boolean;
      reason: string;
      rule: "RULE-FORECAST-1";
    };

export function evaluateBucketAttempt(input: BucketAttempt): BucketDecision {
  const effective = effectiveBucket(input.compositeScore, input.timingPct);
  const flagged = needsManagerFlag(input.timingPct);

  if (BUCKET_RANK[input.attempted] <= BUCKET_RANK[effective]) {
    return { ok: true, effective, flagged };
  }

  const timingLabel =
    input.timingPct == null
      ? "not yet assessed"
      : input.timingPct < 50
        ? "below 50%"
        : `${input.timingPct}%`;

  // Canon display labels. PUSH/LOSS are legacy — display as "Omit".
  const bucketLabels: Record<ForecastBucket, string> = {
    COMMIT: "Commit",
    BEST_CASE: "Best Case",
    PIPELINE: "Pipeline",
    PUSH: "Omit",    // legacy: maps to Omit
    LOSS: "Omit",    // legacy: maps to Omit
    OMITTED: "Omit",
  };

  let reason: string;
  if (input.attempted === "COMMIT" && (input.timingPct ?? 0) < 90) {
    reason = `Timing confidence ${timingLabel} — cannot Commit. RULE-FORECAST-1 requires 90% or higher. Update timing at CP10, or move this deal to ${bucketLabels[effective]}.`;
  } else if (input.attempted === "BEST_CASE" && (input.timingPct ?? 0) < 70) {
    reason = `Timing confidence ${timingLabel} — cannot place in Best Case. RULE-FORECAST-1 requires 70% or higher. Update timing at CP10, or move to ${bucketLabels[effective]}.`;
  } else {
    reason = `Composite score (${input.compositeScore}) is below the floor for ${bucketLabels[input.attempted]}. Highest eligible bucket: ${bucketLabels[effective]}.`;
  }

  return {
    ok: false,
    effective,
    flagged,
    reason,
    rule: "RULE-FORECAST-1",
  };
}

/** Map declared-timing button -> numeric %. 49 represents "<50". */
export function declaredButtonToPct(
  b: "90" | "80" | "70" | "60" | "50" | "below_50" | null,
): number | null {
  if (b == null) return null;
  if (b === "below_50") return 49;
  return Number(b);
}
